import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import { ButtonGroup, Col, Dropdown, Row } from "react-bootstrap";
import { Link, Redirect } from "react-router-dom";
import { isSandbox, isSystemManaged } from "redux-store/networks";
import { isReadonly } from "redux-store/users/selectors";
import { DeleteModal } from "ui/dialog-modal/delete-modal";
import { SENDER_USER } from "ui/filter";
import { ConnectedFullDeviceListTable } from "ui/full-device-list/connected-full-device-list-table";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { QsParamController } from "ui/qs-param-controller";
import { ConnectedWsDeviceStatusConnStatus } from "ui/ws-ds-conn-status/connected-ws-ds-conn-status";
import { getErrorMessage } from "util/response";
import { Page } from "view/layout/page";
import { VIEW } from "view/views";

// TODO(jhohman): refactor to use message framework.
class NetworkViewV2Page extends React.PureComponent {
  static get propTypes() {
    return {
      deleteNetwork: PropTypes.func.isRequired,
      history: PropTypes.object.isRequired,
      location: PropTypes.object.isRequired,
      network: PropTypes.object,
      storesLoaded: PropTypes.bool.isRequired,
      user: PropTypes.object,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      deleting: false,
      errorMessage: "",
      showModal: false,
      successMessage: "",
    };

    this._deleteNetwork = this._deleteNetwork.bind(this);
    this._hideModal = this._hideModal.bind(this);
    this._renderModal = this._renderModal.bind(this);
    this._showModal = this._showModal.bind(this);
    this._submit = this._submit.bind(this);
    this.onFilterStateChange = this.onFilterStateChange.bind(this);
  }

  _showModal() {
    this.setState({
      showModal: true,
    });
  }

  _hideModal() {
    this.setState({ showModal: false });
  }

  _submit() {
    this.setState({ deleting: true }, this._deleteNetwork);

    return Promise.resolve();
  }

  _renderModal() {
    if (!this.state.showModal) {
      return null;
    }

    return (
      <DeleteModal
        objectType="Network"
        onDelete={this._submit}
        onClose={this._hideModal}
        deleteId={this.props.network.id}
      />
    );
  }

  _deleteNetwork() {
    const { deleteNetwork, network } = this.props;
    const { id: networkId } = network;

    if (!networkId) {
      return;
    }

    return deleteNetwork(networkId).catch((e) => {
      this.setState({
        errorMessage: getErrorMessage(e),
        successMessage: "",
        deleting: false,
      });
    });
  }

  onFilterStateChange(filterApi, filterState) {
    const { history, location } = this.props;
    const { pathname } = location;

    if (filterState.sender === SENDER_USER) {
      const qString = filterApi.serializeFilters(filterState);

      history.push({ pathname, search: qString });
    }
  }

  renderToolbox() {
    const { network, user } = this.props;

    if (!network) {
      return null;
    }

    const readonly = isReadonly(user);
    const title = readonly ? "View parameters" : "Update parameters";
    const flagsTitle = readonly ? "View flags" : "Update flags";
    const deleteTitle = "Delete Network";
    const { id: networkId } = network;

    return (
      <div className="page-tools btn-group">
        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="primary" id="dropdown-configure">
            <i className="fa fa-cog" /> Configure
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {!readonly && (
              <Dropdown.Item
                as={Link}
                to={`/networks/${networkId}/${VIEW.EDIT}`}
                key="edit"
              >
                <i className="fa fa-edit mr-2" /> Edit network
              </Dropdown.Item>
            )}
            <Dropdown.Item
              as={Link}
              to={`/networks/${networkId}/configure`}
              key="changeParams"
            >
              <i className="fa fa-list mr-2" /> {title}
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              to={`/networks/${networkId}/flags`}
              key="changeFlags"
            >
              <i className="fa fa-flag mr-2" /> {flagsTitle}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={this._showModal}
              key="deleteNetwork"
              className={classNames({ hide: readonly })}
            >
              <i className="fa fa-times mr-2" /> {deleteTitle}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }

  renderDesc() {
    const { network } = this.props;

    if (!network) {
      return "";
    }

    return (
      <span className="network-status-labels">
        {isSandbox(network) ? (
          <div className="badge bg-gray">Sandbox Network</div>
        ) : (
          <div className="badge bg-green">Production Network</div>
        )}
        {isSystemManaged(network) && (
          <div className="badge bg-red">Ad Platform Synced</div>
        )}
      </span>
    );
  }

  renderView() {
    const { history, location, network, storesLoaded } = this.props;
    const { deleting } = this.state;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!deleting && !network) {
      return <Redirect to="/404" />;
    }

    if (!network) {
      return null;
    }

    return (
      <div>
        <Row>
          <Col xs={12}>
            <ConnectedWsDeviceStatusConnStatus />
          </Col>
        </Row>
        <QsParamController
          location={location}
          qsName="order_by"
          qsValue="status,id"
          noOverride
          onMount
        >
          <QsParamController
            location={location}
            qsName="network_id"
            qsValue={String(network.id)}
          >
            <ConnectedFullDeviceListTable
              history={history}
              location={location}
              networkFilter={false}
              onStateChange={this.onFilterStateChange}
              search
            />
          </QsParamController>
        </QsParamController>
        {this._renderModal()}
      </div>
    );
  }

  render() {
    const { network } = this.props;
    const { deleting, errorMessage, successMessage } = this.state;
    const networkName = network ? network.name : "";
    const desc = this.renderDesc();
    const toolbox = this.renderToolbox();
    const view = this.renderView();

    if (deleting && !network) {
      return <Redirect to="/networks" push={true} />;
    }

    return (
      <Page title={networkName} desc={desc} toolbox={toolbox}>
        <Notification error={errorMessage} success={successMessage} />
        {view}
      </Page>
    );
  }
}

export { NetworkViewV2Page };
